import { type ApiRequestOptions, apiJson } from "@/api/apiClient";
import type {
	DocumentUploadOptions,
	DocumentUploadResponse,
	ProcessingResultsResponse,
	ProcessingStatusResponse,
} from "@/types";

/**
 * Upload a document file to the backend
 */
export const uploadDocument = async (
	file: File,
	options?: DocumentUploadOptions,
	requestOptions?: ApiRequestOptions,
): Promise<DocumentUploadResponse> => {
	const formData = new FormData();
	formData.append("file", file);
	formData.append("title", file.name);

	// Send options as JSON string - backend needs to parse this
	// Note: Backend currently expects req.body.options to be an object
	// If backend doesn't parse JSON strings, it will need to be updated to:
	// const options = typeof req.body.options === 'string'
	//   ? JSON.parse(req.body.options)
	//   : req.body.options;
	if (options) {
		formData.append("options", JSON.stringify(options));
	}

	return apiJson<DocumentUploadResponse>("/documents/jobs/upload", {
		method: "POST",
		body: formData,
		headers: {},
		signal: requestOptions?.signal,
	});
};

/**
 * Get the status of a processing job
 */
export const getJobStatus = async (jobId: string, options?: ApiRequestOptions): Promise<ProcessingStatusResponse> => {
	return apiJson<ProcessingStatusResponse>(`/documents/jobs/status/${jobId}`, {
		signal: options?.signal,
	});
};

/**
 * Get the results of a completed processing job
 */
export const getJobResults = async (jobId: string, options?: ApiRequestOptions): Promise<ProcessingResultsResponse> => {
	return apiJson<ProcessingResultsResponse>(`/documents/jobs/results/${jobId}`, {
		signal: options?.signal,
	});
};

export const cancelDocumentJob = async (jobId: string, options?: ApiRequestOptions): Promise<void> => {
	await apiJson(`/documents/jobs/cancel/${jobId}`, {
		method: "POST",
		signal: options?.signal,
	});
};

export const deleteDocument = async (documentId: string, options?: ApiRequestOptions): Promise<void> => {
	await apiJson(`/documents/${documentId}`, {
		method: "DELETE",
		signal: options?.signal,
	});
};
